import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LeftAngle from '../../../assets/icons/left-angle.svg';
import RightAngle from '../../../assets/icons/right-angle.svg';
import LoadingSpinner from '../../loadingSpinner/loadingSpinner';
import { ColorKey, Colors } from '../../../constants/constants';
import EmptyBigCard from '../dashboardCards/emptyBigCard';
import InfoIconGray from '../../../assets/icons/info-icon-gray.svg';
import ExportButton from '../../exportButton/exportButton';
import { TableColumnInterface } from '../../../interfaces/tableColumn.interface';
import AdsReportTable from '../../../partials/adsReportTable';
import { AdsReportInterface } from '../../../interfaces/adsReport.interface';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { DiggerPlanEnum } from '../../enums/DiggerPlanEnum';
import SearchIcon from '../../../assets/icons/searchIconAds.png';
import OrganizationType from '../../enums/organizationTypesEnum';
import { eCommerceReferrerReportColumns, referrerReportColumns } from '../../referrerReport/referrerReport';
import InfoIcon from '../../../assets/icons/info-icon.svg';
import { useDebounce } from '../../../constants/hookConstants';

interface ListProps {
  cardTitle: string;
  listData?: { ads: AdsReportInterface[] | null; hasNextPage: boolean };
  bgColor: ColorKey;
  page: number;
  setPage: (_page: number) => void;
  isFetching?: boolean;
  isError?: boolean;
  info?: string;
  fetchDownload?: (_acceptHeader: string) => void;
  columns?: TableColumnInterface[];
  fixedColumns?: TableColumnInterface[];
  search?: (_searchValue: string) => void;
}

const List: FC<ListProps> = (props) => {
  const { t } = useTranslation();
  const disabledFields = ['sales', 'cost', 'cvRateSeo', 'reportedNumber'];
  const productId = useOrganizationSettingsStore((state) => state.organizationSettings.productId);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  const [showSearch, setShowSearch] = useState(false);

  const toggleSearch = () => {
    setShowSearch((prev) => !prev);
  };

  const handlePrevPage = () => {
    props.setPage(props.page - 1);
  };

  const handleNextPage = () => {
    props.setPage(props.page + 1);
  };

  if (props.listData?.ads === null && props.page > 0) {
    props.setPage(0);
  }

  const navigateToAd = (id: number) => {
    navigate(`../ad-page/${id}`);
  };

  const debouncedSearch = useDebounce((val: string) => {
    if (props.search) props.search(val);
  }, 500);

  return (
    <div className="relative flex h-full flex-1 flex-col text-clip rounded-lg">
      <div className="relative flex max-h-[47px] w-full items-center justify-between border-b border-dark px-5 py-[15px]">
        <div className="flex w-[500px] items-center gap-4 text-lg  font-medium text-primary xl:text-base">
          {t(props.cardTitle)}
          <Tooltip title={t(props.info || '')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="mx-4 flex w-full max-w-[calc(100%-50px)] items-center justify-between gap-2">
          <div />
          <div className="border-gray-300 relative flex items-center rounded border transition-all duration-500 ease-in-out">
            <button
              onClick={toggleSearch}
              disabled={!props.listData?.ads || props.listData.ads.length === 0 || props.isFetching || props.isError}
              className={`flex h-8 w-8 items-center justify-center rounded transition-all duration-500 ease-in-out hover:bg-gray-200 ${
                showSearch ? 'invisible w-0' : 'visible w-8'
              } ${!props.listData?.ads || props.listData.ads.length === 0 || props.isFetching || props.isError ? 'bg-[#EAEAEA] hover:cursor-not-allowed' : ''}`}
            >
              <img src={SearchIcon} alt="Search Icon" className="h-4 w-4" />
            </button>
            <div
              ref={searchRef}
              className={`outline- flex items-center overflow-hidden transition-all duration-500 ease-in-out ${
                showSearch ? 'w-[150px] px-2' : 'w-0 px-0'
              }`}
            >
              <img
                onClick={props.listData?.ads ? toggleSearch : () => null}
                src={SearchIcon}
                alt="Search"
                className={`absolute left-2 h-4 w-4 text-[#828282] ${!props.listData?.ads || props.listData.ads.length === 0 || props.isFetching || props.isError ? 'hover:cursor-not-allowed' : ' hover:cursor-pointer'}`}
              />
              <input
                type="text"
                className={`h-8 w-full rounded border-none py-1 pl-1 pr-2 text-primary outline-none placeholder:text-gray-400 ${
                  showSearch ? 'visible' : 'invisible'
                }`}
                placeholder={`${t('Search') || 'Search'}`}
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        {' '}
        {props.fetchDownload && (
          <div className="flex w-[220px] justify-center rounded-[6px] border py-1">
            <ExportButton fetchDownload={props.fetchDownload} />
          </div>

        )}
        <div
          className="absolute bottom-0 left-[50%] h-[3px] w-[calc(100%-20px)] translate-x-[-50%] rounded-t-lg"
          style={{ backgroundColor: Colors[props.bgColor] }}
        />
      </div>
      <div className="relative w-full flex-1 flex-col">
        {!props.isFetching && !props.isError && props.listData?.ads === null && (
          <EmptyBigCard />
        )}
        {props.isFetching && <LoadingSpinner center color="primary" />}
        {props.isError && !props.isFetching && (
          <p>
            {t('errorRetrievingData')}
          </p>
        )}
        {!props.isFetching && !props.isError && props.listData?.ads !== null && (
          <div className=" small-scroll max-h-[500px] overflow-x-auto">
            <AdsReportTable
              data={props.listData?.ads}
              isFetching={props.isFetching}
              isError={props.isError}
              columns={props.columns}
              fixedColumns={props.fixedColumns}
              disabledFields={productId === DiggerPlanEnum.standard ? disabledFields : []}
              navigateToAd={navigateToAd}
            />
          </div>
        )}
      </div>
      {!props.isFetching && !props.isError && props.listData?.ads !== null && (
        <div className="mb-[15px] flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handlePrevPage}
            className={`
              ${props.page === 0
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10 
            `}
            disabled={props.page === 0 || props.isFetching}
          >
            <img src={LeftAngle} alt="" />
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            className={`
              ${!props.listData?.hasNextPage
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10
            `}
            disabled={!props.listData?.hasNextPage || props.isFetching}
          >
            <img src={RightAngle} alt="" />
          </button>
        </div>
      )}
    </div>
  );
};
export default List;
